import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import api from '../../../../services/api';
import { ROUTES } from '../../../../constants';

// Aba "Lead Time" do Painel de Gestão (Onda 1 · aprovada).
// Onde o pedido passa o tempo + os que estão envelhecendo agora.

interface IStage {
  count: number;
  p50Hours: number | null;
  p90Hours: number | null;
}

interface ILeadTime {
  stages: {
    toSeparation: IStage;
    toShipping: IStage;
    toDelivery: IStage;
    total: IStage;
  };
  stalled: {
    id: number;
    number: string;
    customerName: string;
    stage: string;
    ageHours: number;
  }[];
}

const formatHours = (hours: number | null) => {
  if (hours === null) return '—';
  if (hours >= 48) return `${Math.round(hours / 24)}d ${Math.round(hours % 24)}h`;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

const formatAge = (hours: number) =>
  hours >= 24 ? `${Math.floor(hours / 24)}d ${hours % 24}h` : `${hours}h`;

const ageChip = (hours: number) =>
  hours >= 24
    ? { background: '#FAE4E4', color: '#C43D3D' }
    : { background: '#FBF0DC', color: '#9A6A0B' };

const STAGE_COLORS = ['#0E52A0', '#2E6CB5', '#6FA0D6'];
const STAGE_LABELS = [
  'Criação → Separação',
  'Separação → Expedição',
  'Expedição → Entrega',
];

export const LeadTimeTab = ({ period }: { period: string }) => {
  const [data, setData] = useState<ILeadTime | null>(null);
  const history = useHistory();
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/summarizers/lead-time?period=${period}`);
      setData(response.data);
    } catch {
      setData(null);
    }
  }, [period]);

  useEffect(() => {
    setData(null);
    load();
    timer.current = setInterval(load, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  const stageList = [
    data.stages.toSeparation,
    data.stages.toShipping,
    data.stages.toDelivery,
  ];
  const flowTotal = stageList.reduce((s, x) => s + (x.p50Hours || 0), 0) || 1;
  const maxBar = Math.max(
    1,
    ...stageList.flatMap((s) => [s.p50Hours || 0, s.p90Hours || 0]),
  );

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Paper style={{ padding: 20 }}>
        <Typography variant="subtitle2" gutterBottom>
          Tempo mediano por etapa — a largura é proporcional ao tempo
        </Typography>
        <Box display="flex" style={{ gap: 2 }}>
          {stageList.map((stage, i) => (
            <div
              key={STAGE_LABELS[i]}
              style={{
                flex: Math.max(stage.p50Hours || 0.4, 0.4),
                background: STAGE_COLORS[i],
                color: '#fff',
                padding: '10px 10px 8px',
                fontSize: 11.5,
                fontWeight: 700,
                minWidth: 90,
                borderRadius:
                  i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : 0,
              }}
            >
              {STAGE_LABELS[i]}
              <div style={{ fontWeight: 600, opacity: 0.85, fontSize: 10.5 }}>
                mediana {formatHours(stage.p50Hours)} · p90 {formatHours(stage.p90Hours)}
              </div>
            </div>
          ))}
        </Box>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: 'block', marginTop: 8 }}
        >
          Lead time total (criação → entrega): mediana{' '}
          <b>{formatHours(data.stages.total.p50Hours)}</b> · p90{' '}
          <b>{formatHours(data.stages.total.p90Hours)}</b> ·{' '}
          {data.stages.total.count} pedidos entregues no período.
        </Typography>
      </Paper>

      <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
        <Paper style={{ flex: '2 1 520px', padding: 16, overflowX: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom>
            Parados agora — envelhecendo além do normal (≥ 4h na etapa)
          </Typography>
          <Table size="small" aria-label="Pedidos parados">
            <TableHead>
              <TableRow>
                <TableCell>Pedido</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Etapa atual</TableCell>
                <TableCell align="right">Parado há</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.stalled.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell style={{ fontWeight: 600 }}>{order.number}</TableCell>
                  <TableCell>{order.customerName}</TableCell>
                  <TableCell>{order.stage}</TableCell>
                  <TableCell align="right">
                    <span
                      style={{
                        ...ageChip(order.ageHours),
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 9px',
                        borderRadius: 999,
                      }}
                    >
                      {formatAge(order.ageHours)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => history.push(ROUTES.order)}
                      aria-label="Abrir pedidos"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {data.stalled.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="textSecondary">
                      Nenhum pedido envelhecendo — fila saudável. ✔
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper style={{ flex: '1 1 300px', padding: 16, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle2" gutterBottom>
            p50 × p90 por etapa
          </Typography>
          {stageList.map((stage, i) => (
            <Box key={STAGE_LABELS[i]} mb={1.2}>
              <Typography variant="caption" color="textSecondary">
                {STAGE_LABELS[i]}
              </Typography>
              {[
                { value: stage.p50Hours, color: STAGE_COLORS[0], label: 'p50' },
                { value: stage.p90Hours, color: '#9A6A0B', label: 'p90' },
              ].map(({ value, color, label }) => (
                <Box key={label} display="flex" alignItems="center" style={{ gap: 8 }}>
                  <span style={{ width: 26, fontSize: 11, color: '#5A6B7A' }}>{label}</span>
                  <div
                    style={{
                      flex: 1,
                      background: '#EDF1F5',
                      borderRadius: 4,
                      height: 12,
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: '0 auto 0 0',
                        width: `${((value || 0) / maxBar) * 100}%`,
                        background: color,
                        borderRadius: 4,
                      }}
                    />
                  </div>
                  <span style={{ width: 52, fontSize: 12, fontWeight: 700, textAlign: 'right' }}>
                    {formatHours(value)}
                  </span>
                </Box>
              ))}
            </Box>
          ))}
          <Typography variant="caption" color="textSecondary">
            p90 muito acima da mediana = fila (poucos pedidos travando muito).
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};
